/**
 * Loading overlay for the Coign widget.
 *
 * Shows progress bar, stage text, and cancel/retry controls during
 * model download and init. Auto-shows on downloadStart, auto-hides
 * on downloadComplete or downloadError. Phase-aware copy for quick/full.
 */

import type { DownloadProgress, CoignError, LoadPhase } from '../types.js';
import { on } from '../events.js';
import { cancelEngineInit, getPresetDownloadMb } from '../core/engine.js';
import { getConfig } from '../config.js';

/* ------------------------------------------------------------------ */
/*  Module state                                                      */
/* ------------------------------------------------------------------ */

let overlayEl: HTMLElement | null = null;
let progressBarEl: HTMLElement | null = null;
let stageTextEl: HTMLElement | null = null;
let phaseLabelEl: HTMLElement | null = null;
let cancelBtnEl: HTMLButtonElement | null = null;
let retryBtnEl: HTMLButtonElement | null = null;
let errorTextEl: HTMLElement | null = null;
let unsubs: Array<() => void> = [];
let retryCallback: (() => void) | null = null;
let currentPhase: LoadPhase = 'quick';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function createEl(tag: string, cls?: string, attrs?: Record<string, string>): HTMLElement {
  const el = document.createElement(tag);
  if (cls) el.className = cls;
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  }
  return el;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return Math.round(bytes / 1024) + ' KB';
}

function phaseTitle(phase: LoadPhase, fromCache?: boolean, downloadMb?: number): string {
  const sizeHint =
    downloadMb && downloadMb > 0 ? ` (~${downloadMb >= 1000 ? (downloadMb / 1000).toFixed(1) + ' GB' : downloadMb + ' MB'})` : '';
  if (phase === 'quick') {
    return fromCache
      ? 'Loading quick model from cache…'
      : `Downloading quick model${sizeHint || ' (~79 MB)'}…`;
  }
  return fromCache
    ? 'Upgrading to full model (cached)…'
    : `Upgrading to full model${sizeHint}…`;
}

/* ------------------------------------------------------------------ */
/*  Build / attach overlay                                            */
/* ------------------------------------------------------------------ */

export interface LoadingOverlayCallbacks {
  onCancel?: () => void;
  onRetry?: () => void;
}

export function attachLoadingOverlay(
  container: HTMLElement,
  callbacks?: LoadingOverlayCallbacks
): void {
  if (overlayEl) return;
  retryCallback = callbacks?.onRetry ?? null;

  overlayEl = createEl('div', 'coign-loading-overlay coign-loading-overlay--hidden');
  overlayEl.setAttribute('role', 'status');
  overlayEl.setAttribute('aria-live', 'polite');

  phaseLabelEl = createEl('div', 'coign-loading-phase');
  phaseLabelEl.textContent = 'Quick model';
  overlayEl.appendChild(phaseLabelEl);

  const track = createEl('div', 'coign-loading-track');
  progressBarEl = createEl('div', 'coign-loading-bar');
  track.appendChild(progressBarEl);
  overlayEl.appendChild(track);

  stageTextEl = createEl('div', 'coign-loading-text');
  stageTextEl.textContent = 'Checking cache…';
  overlayEl.appendChild(stageTextEl);

  errorTextEl = createEl('div', 'coign-loading-error');
  errorTextEl.style.display = 'none';
  overlayEl.appendChild(errorTextEl);

  const btnRow = createEl('div', 'coign-loading-buttons');

  cancelBtnEl = document.createElement('button');
  cancelBtnEl.className = 'coign-loading-btn coign-loading-btn--cancel';
  cancelBtnEl.textContent = 'Cancel';
  cancelBtnEl.addEventListener('click', onCancel);
  btnRow.appendChild(cancelBtnEl);

  retryBtnEl = document.createElement('button');
  retryBtnEl.className = 'coign-loading-btn coign-loading-btn--retry';
  retryBtnEl.textContent = 'Retry';
  retryBtnEl.style.display = 'none';
  retryBtnEl.addEventListener('click', onRetry);
  btnRow.appendChild(retryBtnEl);

  overlayEl.appendChild(btnRow);

  container.appendChild(overlayEl);

  unsubs.push(
    on('downloadStart', ({ phase }) => {
      currentPhase = phase;
      if (phaseLabelEl) {
        phaseLabelEl.textContent = phase === 'quick' ? 'Quick model' : 'Full model';
      }
      const cfg = getConfig();
      const mb =
        phase === 'quick'
          ? getPresetDownloadMb(cfg.quickPreset ?? 'coign-quick')
          : getPresetDownloadMb(cfg.preset || cfg.model || 'coign-lite');
      if (stageTextEl) stageTextEl.textContent = phaseTitle(phase, false, mb);
      show();
    }),
    on('downloadProgress', ({ stage, progress, text, loadedBytes, totalBytes, phase, fromCache }) => {
      if (phase) currentPhase = phase;
      const cfg = getConfig();
      const mb =
        currentPhase === 'quick'
          ? getPresetDownloadMb(cfg.quickPreset ?? 'coign-quick')
          : getPresetDownloadMb(cfg.preset || cfg.model || 'coign-lite');
      updateProgress(stage, progress, text, loadedBytes, totalBytes, fromCache, mb);
    }),
    on('downloadComplete', () => hide()),
    on('downloadError', (err) => showError(err)),
    on('modelReady', () => hide()),
    on('ready', () => hide())
  );
}

export function detachLoadingOverlay(): void {
  unsubs.forEach((u) => u());
  unsubs = [];
  if (overlayEl) {
    overlayEl.remove();
    overlayEl = null;
  }
  progressBarEl = null;
  stageTextEl = null;
  phaseLabelEl = null;
  cancelBtnEl = null;
  retryBtnEl = null;
  errorTextEl = null;
}

function show(): void {
  if (!overlayEl) return;
  overlayEl.classList.remove('coign-loading-overlay--hidden');
  overlayEl.classList.add('coign-loading-overlay--visible');
  if (errorTextEl) errorTextEl.style.display = 'none';
  if (cancelBtnEl) cancelBtnEl.style.display = 'inline-block';
  if (retryBtnEl) retryBtnEl.style.display = 'none';
  if (progressBarEl) progressBarEl.style.width = '0%';
}

function hide(): void {
  if (!overlayEl) return;
  overlayEl.classList.remove('coign-loading-overlay--visible');
  overlayEl.classList.add('coign-loading-overlay--hidden');
}

function updateProgress(
  _stage: DownloadProgress['stage'],
  progress: number,
  text: string,
  loadedBytes?: number,
  totalBytes?: number,
  fromCache?: boolean,
  downloadMb?: number
): void {
  if (!overlayEl) return;
  if (!overlayEl.classList.contains('coign-loading-overlay--visible')) {
    show();
  }

  if (progressBarEl) {
    const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
    progressBarEl.style.width = `${pct}%`;
  }

  if (stageTextEl) {
    let label =
      fromCache !== undefined || !text
        ? phaseTitle(currentPhase, fromCache, downloadMb)
        : text;

    if (fromCache === true) {
      label = phaseTitle(currentPhase, true, downloadMb);
    } else if (fromCache === false && (!text || text === 'Ready')) {
      label = phaseTitle(currentPhase, false, downloadMb);
    }

    if (loadedBytes !== undefined && totalBytes !== undefined && totalBytes > 0) {
      label += ` (${formatBytes(loadedBytes)} / ${formatBytes(totalBytes)})`;
    } else if (progress > 0 && progress < 1) {
      label += ` (${Math.round(progress * 100)}%)`;
    }

    stageTextEl.textContent = label;
  }
}

function showError(err: CoignError): void {
  if (!overlayEl) return;
  show();
  if (progressBarEl) progressBarEl.style.width = '100%';
  if (stageTextEl) stageTextEl.textContent = 'Failed to load model';
  if (errorTextEl) {
    errorTextEl.textContent = err.message || 'An unknown error occurred.';
    errorTextEl.style.display = 'block';
  }
  if (cancelBtnEl) cancelBtnEl.style.display = 'none';
  if (retryBtnEl) retryBtnEl.style.display = 'inline-block';
}

function onCancel(): void {
  cancelEngineInit();
  hide();
}

function onRetry(): void {
  if (retryCallback) {
    retryCallback();
  }
}
